// The one "price includes tax" line, shared by the point-of-sale modals and
// the package card.
//
// Under ADDITIVE tax the modals already print Subtotal, the tax row and the
// Total, because the client is charged more and the breakdown is the only
// way to read the number. Under INCLUSIVE tax nothing was shown at all: the
// charged amount does not change, so there was nothing to reconcile, and the
// tax inside it stayed invisible to whoever was ringing up the sale. Every
// inclusive-pricing jurisdiction prints it on the receipt anyway ("includes
// VAT at 20%", "termasuk PPN"), and this app's own invoice already does, so
// the sale surfaces now say the same thing in the same words.
//
// Deliberately prop-driven rather than reading the settings itself: a modal
// quotes the studio's CURRENT settings (that is what the client is about to
// be charged), while the package card quotes the row's OWN snapshot (what
// the client was charged, which may predate a settings change). One
// component, two sources, identical sentence.
import { Typography } from "antd";
import { TaxMode } from "shared/types/enum";
import { formatCurrency } from "shared/utils";
import { decomposeTax } from "shared/utils/tax";
import { translateWithVars } from "shared/languages";
import { textPx } from "shared/constants/zoom";

export const InclusiveTaxNote = ({
  amount,
  enabled,
  mode,
  rate,
  label,
  style,
}: {
  amount: number;
  enabled?: boolean | null;
  mode?: string | null;
  rate?: number | null;
  label?: string | null;
  style?: React.CSSProperties;
}) => {
  // Nothing to disclose when tax is off, added on top (the breakdown above
  // already says it), rateless, or when no money changes hands: a Rp 0
  // upgrade would otherwise read "includes PPN (11%): Rp 0". The exclusion
  // is ADDITIVE, not "anything but inclusive": a taxed snapshot whose mode
  // was never stored reads as inclusive, the rule the invoice and the
  // server's sale breakdown both follow, so the card and the invoice agree.
  if (!enabled || mode === TaxMode.Additive || !rate || amount <= 0) return null;
  return (
    <Typography.Text
      type="secondary"
      style={{ fontSize: textPx(12), display: "block", marginTop: 4, ...style }}
    >
      {translateWithVars("pdf.tax_included", {
        label: label || "PPN",
        rate,
        amount: formatCurrency(decomposeTax(amount, rate).taxAmount),
      })}
    </Typography.Text>
  );
};
